import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { 
  FiDatabase, FiTable, FiLink, FiShield, 
  FiLock, FiCpu, FiServer, FiKey, FiActivity, 
  FiLayers, FiCheckCircle, FiInfo 
} = FiIcons;

const collections = [
  { name: 'clients', purpose: 'Top-level business entities.', fields: 'id, name, industry, plan_id, status', security: 'Standard PII' },
  { name: 'apps', purpose: 'Client applications.', fields: 'id, client_id, name, origins, provider_id', security: 'Standard PII' },
  { name: 'api_keys', purpose: 'App credentials.', fields: 'id, app_id, key_hash, environment, expiry', security: 'Encrypted or Hashed' },
  { name: 'app_config', purpose: 'Technical metadata.', fields: 'id, app_id, website_url, webhook_url', security: 'Standard PII' },
  { name: 'app_secrets', purpose: 'App-specific masked credentials.', fields: 'id, app_id, name, masked_value', security: 'Encrypted DB' },
  { name: 'secure_vault_secrets', purpose: 'Master system keys.', fields: 'id, name, value, env', security: 'Backend KMS' },
  { name: 'providers', purpose: 'Email service config.', fields: 'id, name, mode, health_score', security: 'References only' },
  { name: 'settings', purpose: 'Global gateway config.', fields: 'id, primary_provider, maintenance_mode', security: 'Admin only' },
  { name: 'templates', purpose: 'Transactional content.', fields: 'id, key, subject, html_body', security: 'Sanitized' },
  { name: 'send_logs', purpose: 'Delivery history.', fields: 'id, app_id, recipient_hash, status', security: 'PII Hashing' },
  { name: 'error_logs', purpose: 'Failure records.', fields: 'id, app_id, error_type, summary', security: 'PII Hashing' },
  { name: 'provider_logs', purpose: 'Health/Heartbeat logs.', fields: 'id, provider_id, latency, status', security: 'Operational' },
  { name: 'usage_counters', purpose: 'Quota tracking.', fields: 'id, target_id, daily, monthly, annual', security: 'High-speed' },
  { name: 'audit_logs', purpose: 'Immutable admin actions.', fields: 'id, user_id, action, target_id', security: 'Write-only' },
  { name: 'suppression_list', purpose: 'Blocked recipients.', fields: 'id, email, reason, scope', security: 'Privacy Compliance' },
  { name: 'otp_policies', purpose: 'Rules for OTP generation.', fields: 'id, app_id, length, expiry', security: 'Operational' },
  { name: 'otp_requests', purpose: 'OTP lifecycle tracking.', fields: 'id, app_id, recipient, code_hash', security: 'Hashed' },
  { name: 'otp_verifications', purpose: 'OTP success/failure.', fields: 'id, request_id, attempts, success', security: 'Fraud prevention' },
  { name: 'scheduled_emails', purpose: 'Queued deliveries.', fields: 'id, app_id, recipient, send_at', security: 'PII Hashing' },
  { name: 'ai_assistant_settings', purpose: 'LLM agent config.', fields: 'id, app_id, model, system_prompt', security: 'Sanitized' },
  { name: 'llm_key_references', purpose: 'Mapping LLM to vault.', fields: 'id, provider_name, vault_ref', security: 'Admin only' },
  { name: 'email_packs', purpose: 'Billing plan definitions.', fields: 'id, name, monthly_limit, price', security: 'Operational' },
  { name: 'deliverability_domains', purpose: 'Sender identity status.', fields: 'id, domain, spf, dkim, dmarc', security: 'DNS Proof' },
  { name: 'security_events', purpose: 'High-priority alerts.', fields: 'id, type, severity, description', security: 'Immediate' }
];

const DataModel = () => {
  return (
    <div className="space-y-8 animate-in fade-in duration-700 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <SafeIcon icon={FiDatabase} className="text-blue-600" /> Data Model Reference
          </h2>
          <p className="text-sm text-slate-500">PocketBase-native control plane and email gateway runtime.</p>
        </div>
        <div className="flex gap-2">
          <span className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-[10px] font-bold uppercase border border-blue-100 flex items-center gap-1.5">
            <SafeIcon icon={FiLayers} /> Unified Schema v1.2
          </span>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-4">
        <SafeIcon icon={FiInfo} className="text-amber-600 text-xl shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 leading-relaxed">
          <b>Integration Note:</b> The admin console authenticates directly with PocketBase. Privileged gateway operations and Emailit delivery run inside PocketBase <code>pb_hooks</code>, with provider secrets stored in PocketHost Secrets so they are never exposed to the browser.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {collections.map(col => (
          <div key={col.name} className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 hover:border-blue-300 transition-all shadow-sm">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiTable} className="text-slate-400" />
              <h4 className="font-bold text-slate-800 font-mono text-xs">{col.name}</h4>
            </div>
            <p className="text-[10px] text-slate-500 leading-tight">{col.purpose}</p>
            <div className="pt-2 border-t border-slate-50">
              <p className="text-[9px] font-black text-slate-400 uppercase">Security</p>
              <p className="text-[10px] font-bold text-blue-600">{col.security}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DataModel;